import React, { createContext, useEffect, useMemo, useState } from 'react';
import Cookies from 'js-cookie';
import { getItem, removeItem, setItem } from '../utility/localStorageControl';
import { updateSocketToken } from '../utility/SocketClient';
import axios from 'axios';
import { useNotification } from './notification.context';

const UserContext = createContext({
    apiClient: axios,
});

export const RoleCanInvite = {
    admin: ['admin', 'manager', 'user', 'temp'],
    manager: ['user', 'temp'],
    user: ['temp'],
    temp: [],
};
export const RoleGovernedResources = [
    'users',
    'departments',
    'mail_senders',
    'notification_templates',
    'active_contacts',
    'imported_contacts',
    'contact_attributes',
    'wa_forms',
];
const TempResources = { events: ['read'], events_attendance: ['get_attendance', 'mark_attendance'] };

const getAllowedResources = (user) => {
    const orgResources = [];
    user?.organization?.accessibleModules?.forEach((module) => {
        module?.moduleResources?.forEach((resource) => orgResources.push(resource));
    });
    if (user?.role === 'admin') {
        return orgResources.map((resource) => ({ ...resource, allowedActions: resource.possibleActions }));
    }
    if (user?.role === 'temp') {
        return orgResources
            .filter((resource) => TempResources[resource.name])
            .map((resource) => ({ ...resource, allowedActions: TempResources[resource.name] }));
    }
    const fromRows = (user?.accesses || [])
        .filter((access) => !RoleGovernedResources.includes(access.resource?.name))
        .map((access) => ({
            ...access.resource,
            allowedActions: access.allowedActions,
        }));
    const fromRole = orgResources
        .filter((resource) => RoleGovernedResources.includes(resource.name))
        .map((resource) => ({
            ...resource,
            allowedActions: user?.role === 'manager' ? resource.possibleActions : ['read'],
        }));
    return [...fromRows, ...fromRole];
};

export const AuthProvider = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(true);
    const { notifyInfo } = useNotification();
    const [token, setToken] = useState(Cookies.get('access_token'));
    const [currentUser, setCurrentUser] = useState(getItem('current_user'));
    const [subscriptionPlan, setSubscriptionPlan] = useState(getItem('user_subscription_plan'));
    const [isLoaded, setIsLoaded] = useState(false);
    const [allowedDepartmentNames, setAllowedDepartmentNames] = useState(
        getItem('current_user')?.departments.map((d) => d.name) || [],
    );
    const [allowedResources, setAllowedResources] = useState(getAllowedResources(getItem('current_user')));

    const apiClient = useMemo(() => {
        const currentUser = getItem('current_user');
        const header = {};
        if (token) {
            header['Authorization'] = 'Bearer ' + token;
        }
        const client = axios.create({
            baseURL: process.env.REACT_APP_API_ENDPOINT,
            // withCredentials: true,

            headers: {
                'Content-type': 'application/json',
                'organization-code': currentUser?.organization?.codeName || 'null',
                Accept: 'application/json',
                ...header,
            },
        });
        client.interceptors.response.use(
            (response) => response,
            (error) => {
                /**
                 * Do something in case the response returns an error code [3**, 4**, 5**] etc
                 * For example, on token expiration retrieve a new access token, retry a failed request etc
                 */
                const { response } = error;
                // const originalRequest = error.config;
                if (response) {
                    //* Handling invalid token or un authorized status
                    if (
                        !window.location.pathname.startsWith('/login') &&
                        (response.status === 401 || response.status === 498)
                    ) {
                        logout();
                        window.location.href = '/';
                    } else {
                        // return response;
                        // throw new Error(error);
                        return Promise.reject(error);
                    }
                }
                return Promise.reject(error);
            },
        );
        return client;
    }, [token, currentUser]);

    const fetchUserSubscriptionPlan = async () => {
        try {
            if (!isAuthenticated || !currentUser) {
                console.error('User not authenticated');
                return;
            }

            setAllowedDepartmentNames(currentUser?.departments.map((d) => d.name) || []);
            setAllowedResources(getAllowedResources(currentUser));
            const res = await apiClient.get(`/api/subscriptions/current-plan`);
            const planData = res?.data?.data;
            if (planData?.[0]) {
                setSubscriptionPlan(planData[0]);
                setItem('user_subscription_plan', planData[0]);
                console.log('Subscription plan:', planData[0]);
            } else {
                setSubscriptionPlan(null);
                setItem('user_subscription_plan', null);
                console.log('No subscription plan found');
            }
        } catch (error) {
            console.error('Failed to fetch subscription plan', error);
        }
    };
    const reloadMe = () => {
        const cacheToken = Cookies.get('access_token');
        const cacheUser = getItem('current_user');
        setCurrentUser(cacheUser);
        if (cacheToken) {
            setToken(cacheToken);
            updateSocketToken(cacheToken);
            setIsLoaded(false);
            apiClient
                .get('/api/org-users/me')
                .then((response) => {
                    const user = response.data.data[0];
                    setCurrentUser(user);
                    setItem('current_user', user);
                    if (!isAuthenticated) {
                        setIsAuthenticated(true);
                    }
                    setIsLoaded(true);
                })
                .catch((err) => {
                    console.error(err);
                    setIsAuthenticated(false);
                    Cookies.remove('access_token');
                    setIsLoaded(true);
                });
        } else {
            setIsAuthenticated(false);
            setIsLoaded(true);
            // window.location.href = '/admin';
        }
    };
    const updateAndSaveAccessToken = (newToken) => {
        setToken(newToken);
        Cookies.set('access_token', newToken, {
            secure: true,
            expires: 1000 * 60 * 60 * 24 * 7, // 7 days
            sameSite: 'lax',
            domain: process.env.REACT_APP_COOKIE_DOMAIN,
        });
        updateSocketToken(newToken);
    };
    const login = async ({ email, otp, orgCode }) => {
        try {
            const loginRes = await apiClient.post(
                '/api/org-users/login/verify-otp',
                {
                    email,
                    otp,
                },
                {
                    headers: {
                        'Content-type': 'application/json',
                        'organization-code': orgCode || 'null',
                        Accept: 'application/json',
                    },
                },
            );
            const accessToken = loginRes.data.token;
            updateAndSaveAccessToken(accessToken);
            if (accessToken) {
                const me = await apiClient.get('/api/org-users/me', {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                });
                const authUser = me.data.data[0];
                if (!authUser) {
                    throw new Error('Invalid Credentials');
                }
                setCurrentUser(authUser);
                setItem('current_user', authUser);
                // await fetchUserSubscriptionPlan();
                setIsAuthenticated(true);
            } else {
                setIsAuthenticated(false);
                throw new Error('Invalid Credentials');
            }
        } catch (err) {
            console.error(err);
            setIsAuthenticated(false);
            if (err.name === 'AxiosError') {
                throw new Error(err.response.data?.message || 'Invalid OTP or credentials');
                // message.error(err.data?.message || 'Invalid Credentials');
            } else {
                throw new Error('Something went wrong');
            }
        }
    };

    const logout = async () => {
        try {
            await apiClient.get('/api/org-users/logout');
            setCurrentUser(null);
            setToken(null);
            Cookies.remove('access_token', {
                domain: process.env.REACT_APP_COOKIE_DOMAIN,
                path: '/',
            });
            updateSocketToken(null);
            removeItem('current_user');
            window.location.href = '/login';
        } catch (err) {
            console.error(err);
        }
    };
    const checkUserAccessToResource = ({ resourceName, action }) => {
        const allowed = allowedResources?.find((resource) => {
            return resource?.name === resourceName && resource?.allowedActions?.includes(action);
        });
        return allowed;
    };
    const updateUserStatus = async (status) => {
        try {
            if (!isAuthenticated || !currentUser) {
                console.error('User not authenticated');
                return;
            }
            const res = await apiClient.put(`/api/org-users/me/update-status`, {
                status,
            });
            notifyInfo({
                message: `You are now ${status}`,
            });
            setCurrentUser({
                ...currentUser,
                status,
            });
        } catch (error) {
            console.error(error);
        }
    };

    useEffect(reloadMe, []);
    useEffect(() => {
        fetchUserSubscriptionPlan();
    }, [isAuthenticated, currentUser?.id]);

    return (
        <UserContext.Provider
            value={{
                allowedResources,
                reloadMe,
                currentUser,
                setCurrentUser,
                login,
                logout,
                apiClient,
                isAuthenticated,
                token,
                allowedDepartmentNames,
                checkUserAccessToResource,
                isLoaded,
                setToken,
                updateUserStatus,
                subscriptionPlan,
                fetchUserSubscriptionPlan,
                updateAndSaveAccessToken,
            }}
        >
            {children}
        </UserContext.Provider>
    );
};

export const useAuth = () => {
    const context = React.useContext(UserContext);
    if (!context) {
        throw new Error('userAuth must be used within a UserProvider');
    }
    return context;
};
